import Button from "../../ui/buttons/Button";
import AttachmentList from "../mailContent/AttachmentList";
import EmailHtmlBody from "../mailContent/EmailHtmlBody";
import LoadingSpinner from "../../ui/spinner/LoadingSpinner";
import {
  formatReceivedAt,
  formatReceivedAtLong,
} from "../../utils/formatReceivedAt";

import styles from "./UnassignedMailDetail.module.css";

const formatAddress = (name, email) =>
  name?.trim() ? `${name.trim()} <${email}>` : email;

const STATUS_NOTES = {
  ignored: "Correo ignorado",
  spam: "Marcado como spam · remitente bloqueado en la casilla",
};

const UnassignedMailDetail = ({
  mail,
  loading = false,
  error = null,
  actionPending = null,
  actionError = null,
  onIgnore,
  onSpam,
  onDelete,
  onRestore,
  onOpenLead,
  onRetry,
}) => {
  if (loading) {
    return (
      <div className={styles.state} role="status">
        <LoadingSpinner size={24} thickness={3} />
        <span>Cargando correo...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.state} role="alert">
        <span>{error}</span>
        <Button variant="primary" tone="blue" height={36} onClick={onRetry}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (!mail) {
    return (
      <div className={styles.state}>
        Elegí un correo de la lista para ver su contenido.
      </div>
    );
  }

  const plainBody = mail.body?.trim() ? (
    <div className={styles.body}>{mail.body}</div>
  ) : (
    <div className={styles.emptyBody}>Este correo no tiene texto.</div>
  );
  const isPending = mail.status === "pending";
  const isResolved = mail.status === "ignored" || mail.status === "spam";
  const busy = Boolean(actionPending);

  return (
    <div className={styles.detail}>
      <h2 className={styles.subject}>{mail.subject || "(Sin asunto)"}</h2>

      <dl className={styles.meta}>
        <dt>De</dt>
        <dd>{formatAddress(mail.from_name, mail.from_email)}</dd>
        {mail.cc?.length > 0 && (
          <>
            <dt>CC</dt>
            <dd>{mail.cc.join(", ")}</dd>
          </>
        )}
        <dt>Cuenta</dt>
        <dd>{mail.mail_account?.email}</dd>
        <dt>Fecha</dt>
        <dd title={formatReceivedAtLong(mail.received_at)}>
          {formatReceivedAt(mail.received_at)}
        </dd>
      </dl>

      <div className={styles.bodyCard}>
        {mail.html_body?.trim() ? (
          <EmailHtmlBody
            html={mail.html_body}
            attachments={mail.attachments || []}
            fallback={plainBody}
          />
        ) : (
          plainBody
        )}
        <AttachmentList
          attachments={mail.attachments || []}
          className={styles.attachments}
        />
      </div>

      {actionError && (
        <p className={styles.error} role="alert">
          {actionError}
        </p>
      )}

      {isPending && (
        <div className={styles.actions}>
          <Button
            variant="secondary"
            tone="neutral"
            height={40}
            onClick={onIgnore}
            disabled={busy}
            loading={actionPending === "ignore"}
          >
            Ignorar
          </Button>
          <Button
            variant="secondary"
            tone="red"
            height={40}
            onClick={onSpam}
            disabled={busy}
            loading={actionPending === "spam"}
          >
            Marcar como spam
          </Button>
        </div>
      )}

      {isResolved && (
        <div className={styles.actions}>
          <span className={styles.statusNote}>
            {STATUS_NOTES[mail.status]}
            {mail.resolved_at
              ? ` · ${formatReceivedAtLong(mail.resolved_at)}`
              : ""}
          </span>
          <div className={styles.actionButtons}>
            {mail.status === "ignored" && (
              <Button
                variant="secondary"
                tone="neutral"
                height={40}
                onClick={onRestore}
                disabled={busy}
                loading={actionPending === "restore"}
              >
                Volver a pendientes
              </Button>
            )}
            <Button
              variant="secondary"
              tone="red"
              height={40}
              onClick={onDelete}
              disabled={busy}
              loading={actionPending === "delete"}
            >
              Eliminar definitivamente
            </Button>
          </div>
        </div>
      )}

      {mail.status === "assigned" && (
        <div className={styles.actions}>
          <span className={styles.statusNote}>
            Vinculado a <b>{mail.lead?.name}</b>
            {mail.resolved_at
              ? ` · ${formatReceivedAtLong(mail.resolved_at)}`
              : ""}
          </span>
          {mail.lead?.id && (
            <Button
              variant="primary"
              tone="blue"
              height={40}
              onClick={() => onOpenLead?.(mail.lead.id)}
            >
              Abrir lead →
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default UnassignedMailDetail;
